import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS } from '../../../../utils/receivingConfig'

const T = 'receiving_config.settings'
const inputClasses = 'shadow-sm block w-full sm:text-sm rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
const FIRST_SAVE_KEYS = ['dock_count', 'units_per_door_franja', 'daily_processing_man_hours']
const STEP = { daily_processing_man_hours: '0.01' }

// Number(v) alone would let a partial/garbage numeric string (e.g. "-" mid-typing a
// negative number, or a stray letter) resolve to NaN, which is not null — every "!==
// null" bounds check below would then skip it silently, including the firstSave
// 'required' check, so an invalid value would show no error at all. Folding NaN into
// null makes such a value behave like an absent one: no false-valid range check, and a
// correct 'required' error where applicable.
const num = v => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export const validateSettings = (values = {}, { firstSave = false } = {}) => {
  const errors = {}
  if (firstSave)
    FIRST_SAVE_KEYS.forEach(k => {
      if (num(values[k]) === null) errors[k] = 'required'
    })
  const dock = num(values.dock_count)
  if (dock !== null && (dock < 1 || dock > 50)) errors.dock_count = 'dock_count_range'
  const mh = num(values.daily_processing_man_hours)
  if (mh !== null && mh < 0) errors.daily_processing_man_hours = 'non_negative'
  else if (mh !== null && mh > 9999.99) errors.daily_processing_man_hours = 'man_hours_max'
  ;['units_per_door_franja', 'no_show_grace_minutes', 'reschedule_cutoff_hours', 'express_buffer_door_franjas'].forEach(k => {
    const v = num(values[k])
    if (v !== null && v < 0) errors[k] = 'non_negative'
  })
  const uph = num(values.checkin_units_per_hour_default)
  if (uph !== null && uph <= 0) errors.checkin_units_per_hour_default = 'positive'
  const buffer = num(values.express_buffer_door_franjas)
  if (buffer !== null && dock !== null && buffer > dock) errors.express_buffer_door_franjas = 'buffer_exceeds_docks'
  return errors
}

const Suggestion = ({ t, suggestion, onApply }) => {
  if (!suggestion) return null
  if (suggestion.status !== 'ok') return <span className="text-xs text-gray-400">{t(`${T}.no_suggestion`)}</span>
  return (
    <span className="text-xs text-gray-600">
      {t(`${T}.suggestion`, { value: suggestion.value })}{' '}
      <button type="button" className="text-indigo-600 hover:underline" onClick={() => onApply(suggestion.value)}>
        {t(`${T}.apply_suggestion`)}
      </button>
    </span>
  )
}

export const SettingsCard = ({ values = {}, onChange, suggestions = {}, requiredKeys = [], errors = {} }) => {
  const { t } = useTranslation()
  return (
    <section className="bg-white border border-gray-200 rounded-md p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-3">{t(`${T}.title`)}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SETTINGS_KEYS.map(key => (
          <div key={key}>
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-gray-700" htmlFor={`setting-${key}`}>
                {t(`${T}.fields.${key}`)}
              </label>
              {requiredKeys.includes(key) && (
                <span className="ml-1 text-xs text-red-600" aria-hidden="true">
                  {t(`${T}.required`)}
                </span>
              )}
            </div>
            <input
              id={`setting-${key}`}
              type="number"
              step={STEP[key] || '1'}
              className={inputClasses}
              value={values[key] ?? ''}
              onChange={e => onChange(key, e.target.value)}
              aria-required={requiredKeys.includes(key)}
            />
            <div className="mt-1 flex justify-between">
              <Suggestion t={t} suggestion={suggestions[key]} onApply={v => onChange(key, v)} />
              {errors[key] && errors[key] !== 'required' && <span className="text-red-500 text-xs">{t(`${T}.errors.${errors[key]}`)}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
